import {
    Component,
    State,
    Prop,
    Host,
    Element,
    Listen,
    h,
    Watch,
} from "@stencil/core";
import { waitForHydrated } from "@trumpf/ux.utils";
import api from "../../utils/api";
import { dispatchEvent } from "../../utils/dispatchEvent";
import { linkWrapperIgnoreAttribute } from "../link-wrapper/attributes";
import { scrollToTarget } from "../../utils/scrollToTarget";

import { SuccessStoriesResponse } from "../../../scripts/mock-generators/success-stories";

@Component({
    tag: "ux-iws-data-controller-success-stories",
    styleUrl: "data-controller-success-stories.scss",
})
export class DataControllerSuccessStories {
    private baseClass = "ux-iws-data-controller-success-stories";
    private teaserFilteredAttribute = "data-ux-success-story-hidden";
    private checkboxTag = "ux-checkbox-list-item";
    private resultsContainer: HTMLElement = null;
    private counterContainer: HTMLElement = null;
    private filterElement: HTMLUxFilterElement = null;

    @Element() element: HTMLUxIwsDataControllerSuccessStoriesElement;

    /** the url to which the request will be made for the results */
    @Prop() src: string = null;
    /** an optional text that indicated the loading state */
    @Prop() loadingText: string = "";
    /** the css selector of the ux-filter element that does the results filtering */
    @Prop() filterSelector: string = null;
    /** the css selector of the element that contains the number of the results */
    @Prop() counterSelector: string = null;
    /** optional text that can be shown to a user in case of an error */
    @Prop() errorText: string = "";
    /** optional form data element to be passed in the POST request */
    @Prop() formDataAttribute = null;
    /** filter attribute identifier for the boxes and tags */
    @Prop() filterAttributeIdentifier: string = "success-story";
    /** if set, the component expects to have already a set of filterable results in the results container slot */
    @Prop() synchronous: boolean = false;

    private teaserFilterAttribute = null;
    private tagFilterAttribute = null;

    @State() error: boolean = false;
    @State() isLoading: boolean = !this.synchronous ? true : false;
    @State() availableFilters: string[] = [];
    @State() selectedFilters: string[] = [];
    @State() triggerCounterUpdate: boolean = false;
    @State() firstCounter: boolean = true;

    @Watch("error")
    hideLoadingBar() {
        if (this.error === true) this.isLoading = false;
    }

    async componentWillLoad() {
        this.teaserFilterAttribute = `data-ux-${this.filterAttributeIdentifier}-filter`;
        this.tagFilterAttribute = `data-ux-${this.filterAttributeIdentifier}-tag`;
        this.resultsContainer = this.element.querySelector(
            "*[slot='results-container']"
        );
        this.filterElement = document.querySelector(this.filterSelector);
        if (!this.resultsContainer) {
            this.error = true;
            return;
        }
        this.setSelectedFilters();
        if (!this.synchronous) {
            this.getDataContent();
        }
    }

    componentDidLoad() {
        this.handleDisabledFilters();
        if (this.synchronous) {
            this.filterItems();
            this.setAvailableFilters();
            this.setCounter();
        }
    }

    private getDataContent = async () => {
        try {
            if (isMockUrl(this.src))
                await new Promise((resolve) => setTimeout(resolve, 2000));
            let response: SuccessStoriesResponse;
            if (isMockUrl(this.src))
                response = await api<SuccessStoriesResponse>(this.src);
            else {
                let uidList = "";
                let item: { uid: number };
                //@ts-expect-error --> global variable is (should be) present
                const resultsList = window.ResultLists;
                if (!resultsList) return;
                for (let i = 0; i < resultsList.length; i++) {
                    item = resultsList[i];
                    uidList += (uidList.length ? "," : "") + item.uid;
                }
                const formData = new FormData();
                const formDataAttribute =
                    this.formDataAttribute === null
                        ? "tx_trumpfspecials_resultlistcustomersuccessstories[uidList]"
                        : this.formDataAttribute;
                formData.append(formDataAttribute, uidList);
                response = await api<SuccessStoriesResponse>(this.src, {
                    method: "POST",
                    body: formData,
                });
            }
            if (response.status === "success") {
                if (
                    response?.data?.elements &&
                    response.data.elements.length > 0
                ) {
                    const htmlArray = response.data.elements.map((element) =>
                        typeof element.html === "string" ? element.html : ``
                    );
                    this.resultsContainer.insertAdjacentHTML(
                        "beforeend",
                        htmlArray.join("")
                    );
                    this.isLoading = false;
                    this.filterItems();
                    this.setAvailableFilters();
                    this.setCounter();
                    const lastTeaser = this.element.querySelector(
                        "ux-iws-teaser-image:last-of-type"
                    ) as HTMLElement;
                    if (lastTeaser) {
                        waitForHydrated(lastTeaser, () => {
                            dispatchEvent("resize");
                        });
                    }
                } else {
                    this.error = true;
                }
            } else if (response.status === "invalid") {
                this.error = true;
            }
        } catch {
            this.error = true;
        }
    };

    async componentDidRender() {
        if (this.triggerCounterUpdate) {
            dispatchEvent("resize");
            this.setAvailableFilters();
            this.setCounter();
            this.triggerCounterUpdate = false;
        }
    }

    private setCounter = () => {
        const visibleTeaserCount = this.element.querySelectorAll(
            `*[${this.teaserFilterAttribute}]:not([${this.teaserFilteredAttribute}])`
        ).length;
        const counter = this.counterContainer.querySelector(
            this.counterSelector
        );
        if (!counter) return;
        counter.innerHTML = String(visibleTeaserCount);
    };

    private setAvailableFilters() {
        const visibleTeasers = Array.from(
            this.element.querySelectorAll(
                `*[${this.teaserFilterAttribute}]:not([${this.teaserFilteredAttribute}])`
            )
        );
        const filtersArray = [];
        visibleTeasers.forEach((item: HTMLElement) => {
            const filtersValues = item
                .getAttribute(this.teaserFilterAttribute)
                .split(",");
            filtersValues.forEach((filterValue: string) => {
                if (!filtersArray.includes(filterValue))
                    filtersArray.push(filterValue);
            });
        });
        this.availableFilters = [...filtersArray];
    }

    @Watch("availableFilters")
    handleDisabledFilters() {
        if (!this.filterElement) {
            this.error = true;
            return;
        }
        const checkboxListItems = Array.from(
            this.filterElement.querySelectorAll(this.checkboxTag)
        );
        checkboxListItems.forEach((item: HTMLUxCheckboxListItemElement) => {
            if (!this.availableFilters.includes(item.value)) {
                item.setAttribute("disabled", "true");
            } else {
                item.removeAttribute("disabled");
            }
        });
    }

    @Watch("selectedFilters")
    filterItems() {
        const teasers = Array.from(
            this.element.querySelectorAll(`*[${this.teaserFilterAttribute}]`)
        );
        teasers.forEach((teaser: HTMLElement) => {
            if (this.selectedFilters.length === 0) {
                teaser.removeAttribute(this.teaserFilteredAttribute);
            } else {
                const teaserFilterAttributes = teaser
                    .getAttribute(this.teaserFilterAttribute)
                    .split(",");
                const isSelected = this.selectedFilters.every(
                    (filter: string) => teaserFilterAttributes.includes(filter)
                );
                if (isSelected)
                    teaser.removeAttribute(this.teaserFilteredAttribute);
                else teaser.setAttribute(this.teaserFilteredAttribute, "");
            }
        });
    }

    @Listen("uxFilterChange", { target: "window" })
    setSelectedFilters(e: CustomEvent = null) {
        if (e?.target !== this.filterElement && e !== null) return;
        const checkedFilters = Array.from(
            this.filterElement.querySelectorAll(this.checkboxTag)
        );
        const checkedCheckboxesValues = [];
        checkedFilters.forEach((item: HTMLUxCheckboxListItemElement) => {
            waitForHydrated(item, () => {
                const checkbox = item.querySelector("input");
                if (checkbox.checked)
                    checkedCheckboxesValues.push(checkbox.value);
            });
        });
        this.selectedFilters = [...checkedCheckboxesValues];
        this.triggerCounterUpdate = true;
    }

    @Listen("click", { target: "window" })
    tagFilterListener(e: Event) {
        const eTarget = e.target as HTMLElement;
        if (!eTarget.hasAttribute(linkWrapperIgnoreAttribute)) return;
        if (!eTarget.hasAttribute(this.tagFilterAttribute)) return;
        const tagFilterValue = eTarget.getAttribute(this.tagFilterAttribute);
        const filterCheckbox = this.filterElement.querySelector(
            `${this.checkboxTag}[value="${tagFilterValue}"] input`
        ) as HTMLInputElement;
        if (!filterCheckbox || filterCheckbox?.checked) return;
        else filterCheckbox.click();
        scrollToTarget({
            target: this.filterElement,
            additionalOffsetTop: 40,
            smooth: true,
        });
    }

    render() {
        return (
            <Host class={this.baseClass}>
                <style>
                    {`
                        .ux-filter${this.filterSelector} .ux-checkbox-list-item[disabled="true"] {
                            display: none;
                        }
                    `}
                </style>
                <div ref={(el) => (this.counterContainer = el)}>
                    <div
                        style={
                            this.isLoading
                                ? {
                                      visibility: "hidden",
                                  }
                                : null
                        }
                    >
                        <slot name="counter" />
                    </div>
                </div>
                {!this.isLoading && this.error && (
                    <div class="ux-u-text-align--center">
                        {this.errorText !== "" && (
                            <div class="ux-u-mt-s">
                                <span class="text-results">
                                    {this.errorText}
                                </span>
                            </div>
                        )}
                        <ux-icon
                            name="close"
                            size-s="60"
                            class="ux-color-error ux-u-mt-s"
                        ></ux-icon>
                        <div style={{ height: "40vh" }}></div>
                    </div>
                )}
                <slot name="results-container"></slot>
                {this.isLoading && !this.error && (
                    <div class="ux-u-text-align--center">
                        {this.loadingText !== "" && (
                            <div class="ux-u-mx-s">
                                <span class="text-results">
                                    {this.loadingText}
                                </span>
                            </div>
                        )}
                        <div class="ux-u-mx-s">
                            <ux-loader variant="spinner"></ux-loader>
                        </div>
                        <div style={{ height: "40vh" }}></div>
                    </div>
                )}
            </Host>
        );
    }
}

function isMockUrl(url: string) {
    return (
        url.indexOf("/mock-data/") > -1 ||
        url.indexOf("/generated-mock-data/") > -1
    );
}
